import React, { FC, useEffect, useState } from 'react';
import { Col, Row, Skeleton } from 'antd';
import { WarningCircle } from 'common/svg';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { getMonitorSalesCategorySalesModel } from 'app/store/reducers/monitorSales.reducer';
import { IMonitorSalesParams } from 'entities/MonitorSales/MonitorSales.models';
import { ChangeArrow } from 'entities/MonitorSales/components/ChangeArrow';

const NAME_SPAN = 3;
const BAR_SPAN = 24 - NAME_SPAN;

const computeScale = (items: { salesPercent: number }[]) => {
  const dataMax = items.length > 0 ? Math.max(...items.map((c) => c.salesPercent)) : 20;
  // Step is chosen first; scaleMax is then rounded to a multiple of step.
  // This guarantees space-between label positions align exactly with grid lines.
  const rawMax = dataMax + 5;
  const step = rawMax <= 30 ? 5 : 10;
  const scaleMax = Math.ceil(rawMax / step) * step;
  const marks = Array.from({ length: scaleMax / step + 1 }, (_, i) => i * step);

  return { scaleMax, step, marks };
};

interface IComponentProps {
  params?: IMonitorSalesParams;
}

export const CategorySalesBlock: FC<IComponentProps> = ({ params }) => {
  const dispatch = useAppDispatch();
  const { monitorSalesCategorySalesModel, monitorSalesCategorySalesModelLoading } = useAppSelector(
    (state) => state.monitorSales
  );
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  useEffect(() => {
    dispatch(getMonitorSalesCategorySalesModel(params));
  }, [params?.storeCode, params?.week]);

  const categories = monitorSalesCategorySalesModel?.categories ?? [];
  const { scaleMax, marks } = computeScale(categories);
  // Grid lines as % of bar area, scaled to scaleMax
  const gridPositions = marks.slice(1).map((m) => (m / scaleMax) * 100);

  return (
    <div className="ms-block">
      <div className="ms__header">
        <span className="ff-medium fs-xs">Category Sales</span>
        <WarningCircle />
      </div>

      <div className="ms__body">
        {monitorSalesCategorySalesModelLoading ? (
          <Skeleton active paragraph={{ rows: 7 }} title={false} />
        ) : (
          <div className="ms-category__chart">
            {/* Vertical dashed grid lines — span all rows, start at bar column */}
            <div className="ms-category__grid" style={{ left: `${(NAME_SPAN / 24) * 100}%` }}>
              {gridPositions.map((pos) => (
                <div key={pos} className="ms-category__grid-line" style={{ left: `${pos}%` }} />
              ))}
            </div>

            {/* Bar rows — bar width scaled to scaleMax */}
            {categories.map((item, index) => {
              const barPct = (item.salesPercent / scaleMax) * 100;
              const isDimmed = hoveredIndex !== null && hoveredIndex !== index;

              return (
                <Row key={item.name} gutter={12} align="middle" className="mb-3">
                  <Col span={NAME_SPAN}>{item.name}</Col>
                  <Col
                    span={BAR_SPAN}
                    className={`ms-category__bar-col ${isDimmed ? 'ms-category__bar-col--dimmed' : ''}`}
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                  >
                    <div className="ms-category__bar-fill" style={{ width: `${barPct}%` }} />
                    <div className="ms-category__bar-label" style={{ left: `calc(${barPct}% + 8px)` }}>
                      <span className="ms-category__pct">{item.salesPercent}%</span>{' '}
                      <span className={`ms-category__vs ms-category__vs--${item.vsLastPeriod >= 0 ? 'pos' : 'neg'}`}>
                        {item.vsLastPeriod >= 0 ? '+' : ''}
                        {item.vsLastPeriod}%
                      </span>
                      <ChangeArrow up={item.vsLastPeriod >= 0} />
                    </div>
                  </Col>
                </Row>
              );
            })}

            {/* Scale labels at bottom */}
            <Row>
              <Col span={NAME_SPAN} />
              <Col span={BAR_SPAN}>
                <div className="ms-category__scale">
                  {marks.map((m) => (
                    <span key={m}>{m}%</span>
                  ))}
                </div>
              </Col>
            </Row>
          </div>
        )}
      </div>
    </div>
  );
};
